import { FlexBox, Spinner, Text } from '@dixa/lib-shared-habla';
import { User } from 'lib-shared-types-api';
import { PropsWithChildren } from 'lib-shared-types-utils';
import { useEffect } from 'react';
import { Waypoint } from 'react-waypoint';

import { useReadSearchInfiniteQuery } from 'apis/kraken';
import { pubSub, PubSubSubscriptionDispose } from 'utils/pubSub';
import { useAppPathParams } from 'utils/url/useAppPathParams';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import { ContactsUser } from './components/user/contactsUser';

function ContactListMeta({ children }: PropsWithChildren) {
  return (
    <FlexBox justifyContent="center" alignItems="center" style={{ height: 72 }}>
      {children}
    </FlexBox>
  );
}

export function ContactsUsers(): JSX.Element {
  const { id } = useAppPathParams('/contacts/user/:id');

  const {
    params: { q: searchTerm = '' },
  } = useAppSearchParams('/contacts/user/:id');

  const {
    results: users,
    isLoading,
    isFetching,
    isSuccess,
    isError,
    hasNextPage,
    fetchNextPage,
    updateResults,
  } = useReadSearchInfiniteQuery({
    scope: 'users',
    query: searchTerm,
    sortBy: { field: 'display_name', order: 'asc' },
    pageSize: 50,
  });

  useEffect(() => {
    const unsubscribe = pubSub.subscribe('userModalUserAdded', (user) => {
      updateResults((draft) => {
        draft.push(user);
      });
    });

    return () => unsubscribe();
  }, [updateResults]);

  useEffect(() => {
    const updateUser = (user: User) => {
      const idx = users.findIndex(({ id }) => id === user.id);

      if (idx !== -1) {
        updateResults((draft) => {
          const idx = draft.findIndex(({ id }) => id === user.id);

          if (idx !== -1) {
            draft[idx] = {
              ...draft[idx],
              email: user.email ?? null,
              phone_number: user.phone_number ?? null,
              name: user.name ?? null,
              company_id: user.company_id ?? null,
              avatar_url: user.avatar_url ?? null,
              type: user.type,
            };
          }
        });
      }
    };

    const subscriptions: PubSubSubscriptionDispose[] = [
      pubSub.subscribe('userModalUserUpdated', updateUser),
      pubSub.subscribe('mergeContactsModalUserMerged', updateUser),
    ];

    return () => {
      subscriptions.forEach((unsubscribe) => unsubscribe());
    };
  }, [updateResults, users]);

  if (isError) {
    return (
      <ContactListMeta>
        <Text.Paragraph>
          An error occurred while loading the users
        </Text.Paragraph>
      </ContactListMeta>
    );
  }

  if (isLoading && users.length === 0) {
    return (
      <ContactListMeta>
        <Spinner />
      </ContactListMeta>
    );
  }

  if (isSuccess && users.length === 0) {
    return (
      <ContactListMeta>
        <Text.Paragraph>No users found</Text.Paragraph>
      </ContactListMeta>
    );
  }

  return (
    <>
      {users.map((user) => (
        <ContactsUser key={user.id} user={user} selected={user.id === id} />
      ))}

      {isFetching && (
        <ContactListMeta>
          <Spinner />
        </ContactListMeta>
      )}

      {hasNextPage && !isFetching && <Waypoint onEnter={fetchNextPage} />}
    </>
  );
}
